import { BALANCE } from '../config/balance';
import type { EnemyKind } from '../core/types';
import { initEnemy, type Enemy } from './enemies';
import { spawnFoeBolt } from './foe-bolts';
import { spawnCore, spawnGem, spawnHeart } from './gems';
import { spawnCoin } from './coins';
import type { Engine } from './engine';

/** 刷怪（含同屏上限与刷怪日志）。 */
export function spawnEnemy(
  eng: Engine, kind: EnemyKind, x: number, y: number,
): void {
  let alive = 0;
  for (const e of eng.enemyPool.all()) if (e.active) alive++;
  if (alive >= (eng.headless ? 150 : BALANCE.spawn.maxAlive)) return;
  const e = eng.enemyPool.obtain();
  initEnemy(e, kind, x, y, eng.time, eng.streams.world);
  const eliteLike = kind === 'elite' || kind === 'boss1' || kind === 'boss2';
  const hpMul = eliteLike ? eng.variant.eliteBossHpMul : eng.variant.enemyHpMul;
  if (hpMul !== 1) {
    e.hp *= hpMul;
    e.maxHp *= hpMul;
  }
  eng.spawnLog.push(`${kind}@${Math.round(eng.time * 10) / 10}`);
}

/** Boss 在玩家周围环上入场，附带命中冻结与事件。 */
export function spawnBoss(eng: Engine, kind: EnemyKind): void {
  const a = eng.streams.world.range(0, Math.PI * 2);
  const r = 380;
  spawnEnemy(
    eng, kind,
    eng.player.x + Math.cos(a) * r,
    eng.player.y + Math.sin(a) * r,
  );
  eng.addFreeze(BALANCE.juice.hitstopBoss);
  eng.emit({ type: 'boss', x: eng.player.x, y: eng.player.y, text: kind });
}

/** 吐弹怪朝玩家开火。 */
export function fireSpit(eng: Engine, e: Enemy): void {
  const c = BALANCE.enemies.spitter;
  const angle = Math.atan2(eng.player.y - e.y, eng.player.x - e.x);
  spawnFoeBolt(eng.foeBoltPool, {
    x: e.x, y: e.y, angle,
    speed: c.boltSpeed, damage: e.damage, radius: c.boltRadius,
    source: { type: 'spit', enemyId: e.id },
  });
}

/** Boss 技能弹幕。 */
export function fireBossBolt(
  eng: Engine, angle: number, speed: number, e: Enemy, skill: string,
): void {
  spawnFoeBolt(eng.foeBoltPool, {
    x: e.x, y: e.y, angle, speed,
    damage: e.damage, radius: BALANCE.enemies.spitter.boltRadius,
    source: { type: 'boss', enemyId: e.id, skill },
  });
}

/** Boss 召唤一圈近战小怪。 */
export function summonMinions(eng: Engine, e: Enemy, n: number): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    spawnEnemy(eng, 'chaser', e.x + Math.cos(a) * 60, e.y + Math.sin(a) * 60);
  }
}

/** 击杀掉落：经验宝石、金币、精英进化核心。 */
export function dropLoot(eng: Engine, e: Enemy): void {
  const x = BALANCE.xp;
  const vt = eng.variant;
  const gemValue =
    e.kind === 'chaser' ? x.gemChaser
    : e.kind === 'elite' ? Math.round(x.gemElite * vt.eliteGemMul)
    : e.kind === 'charger' ? x.gemCharger
    : e.kind === 'bomber' ? x.gemBomber
    : e.kind === 'spitter' ? x.gemSpitter
    : 0;
  for (let i = 0; i < gemValue; i++) {
    const g = eng.gemPool.obtain();
    spawnGem(g, e.x, e.y, 1, eng.streams.drops);
  }
  if (e.kind === 'elite') {
    const g = eng.gemPool.obtain();
    spawnCore(g, e.x, e.y);
  }
  const g = BALANCE.gold;
  let amount = 0;
  if (e.kind === 'elite') {
    amount = Math.round(g.eliteBase * eng.variant.eliteGoldMul);
  } else if (eng.streams.drops.chance(g.dropChance * eng.variant.dropChanceMul)) {
    amount = 1;
  }
  if (amount > 0) {
    const c = eng.coinPool.obtain();
    const total = amount * eng.player.stats.goldMul * eng.variant.goldMul;
    spawnCoin(c, e.x, e.y, total, eng.streams.drops);
  }
  const h = BALANCE.heal;
  const guaranteed = e.kind === 'elite' || e.kind === 'boss1' || e.kind === 'boss2';
  if (guaranteed || eng.streams.drops.chance(h.dropChance)) {
    spawnHeart(eng.gemPool.obtain(), e.x, e.y, eng.streams.drops);
  }
  if (e.kind === 'boss2') {
    spawnHeart(eng.gemPool.obtain(), e.x, e.y, eng.streams.drops);
  }
}
